import { useState } from 'react'
import { api } from '../api/client'
import type { AnalysisResult, KPIResult, MetricSeries } from '../api/types'
import { useAsync } from '../api/useReport'
import { SkeletonChart, SkeletonPage, SkeletonTiles } from '../components/Skeleton'
import { Banner, QualityBanner } from '../components/Banner'
import { Findings } from '../components/Findings'
import { TrendChart } from '../components/TrendChart'
import { WindowPicker } from '../components/WindowPicker'
import { deltaTone, formatChange, formatDate, formatMetric, formatRange, metricLabel } from '../format'

interface Props {
  end: string
  days: number
  maxDate: string
  propertyId?: string
  /** The day this view was opened from, if it was reached from the daybook. */
  fromDay?: string
  onBack: () => void
  onWindowChange: (next: { end?: string; days?: number }) => void
}

const ARROW = { up: '↑', down: '↓', flat: '→' } as const

/**
 * The range view — the one place in the app whose unit is a window rather than a day.
 *
 * Master–detail rather than a grid of charts: the metric list on the left *is* the
 * navigation, and one chart is drawn at a time. A grid that reflows on `auto-fit`
 * decides its own layout by arithmetic and gives every metric the same weight, which
 * is precisely the thing this view exists to stop doing.
 *
 * Everything here comes from a single `/api/report` call — kpis, series and findings —
 * so switching metric is free and never refetches.
 */
export function Metrics({ end, days, maxDate, propertyId, fromDay, onBack, onWindowChange }: Props) {
  const [selected, setSelected] = useState<string | null>(null)

  const { data, error, loading } = useAsync<AnalysisResult>(
    (signal) => api.report({ end, days, propertyId }, signal),
    [end, days, propertyId],
    true,
    `report:${propertyId ?? ''}:${end}:${days}`,
  )

  return (
    <>
      <div className="context-bar">
        <button type="button" className="link-button link-button--back" onClick={onBack}>
          ← {fromDay ? `Back to ${formatDate(fromDay, 'long')}` : 'Back to the daybook'}
        </button>
        <WindowPicker
          end={end}
          days={days}
          maxDate={maxDate}
          onChange={onWindowChange}
          disabled={loading}
        />
      </div>

      {error && <Banner tone="critical">{error}</Banner>}
      {loading && (
        <SkeletonPage label="Loading the window…">
          <SkeletonTiles count={5} />
          <SkeletonChart />
        </SkeletonPage>
      )}

      {data && !loading && (
        <MetricsBody
          data={data}
          selected={selected ?? defaultMetric(data)}
          onSelect={setSelected}
        />
      )}
    </>
  )
}

/**
 * Which metric leads before anyone chooses.
 *
 * The preset's `findings_metric` is the one the property was configured to explain
 * movements in, which makes it the closest thing to a declared primary metric — a
 * better default than "whichever KPI happens to be first in the list".
 */
function defaultMetric(data: AnalysisResult): string {
  const preferred = data.profile.preset.findings_metric
  if (data.series.some((series) => series.metric === preferred)) return preferred
  return data.series[0]?.metric ?? data.kpis[0]?.name ?? ''
}

function MetricsBody({
  data,
  selected,
  onSelect,
}: {
  data: AnalysisResult
  selected: string
  onSelect: (metric: string) => void
}) {
  const series = data.series.find((item) => item.metric === selected)
  const kpi = data.kpis.find((item) => item.name === selected)
  const seriesFor = new Map(data.series.map((item) => [item.metric, item]))

  return (
    <>
      <QualityBanner quality={data.quality} />

      <div className="metrics">
        <nav className="metrics__list" aria-label="Metrics">
          <p className="metrics__list-head">Metric</p>
          {data.kpis.map((item) => (
            <MetricRow
              key={item.name}
              kpi={item}
              series={seriesFor.get(item.name)}
              selected={item.name === selected}
              onSelect={() => onSelect(item.name)}
            />
          ))}
          <p className="metrics__list-note">
            Pick a metric to chart it. All {data.kpis.length} come from one request.
          </p>
        </nav>

        <div className="metrics__detail">
          <div className="metrics__detail-head">
            <div>
              <h1 className="metrics__title">{metricLabel(selected)}</h1>
              {kpi && (
                <>
                  <div className="metrics__value">
                    <span title={formatMetric(kpi.name, kpi.current, 'full')}>
                      {formatMetric(kpi.name, kpi.current)}
                    </span>
                    <span className={`metrics__delta tile__delta--${deltaTone(kpi.change_percent, kpi.name)}`}>
                      {formatChange(kpi.change_percent)}
                    </span>
                  </div>
                  <p className="section__note">
                    vs {formatMetric(kpi.name, kpi.previous, 'full')} over{' '}
                    {formatRange(data.profile.window.comparison_start, data.profile.window.comparison_end)}
                  </p>
                </>
              )}
            </div>
          </div>

          {series ? (
            <TrendChart series={series} comparisonLabel="Previous period" />
          ) : (
            <div className="card">
              <p className="placeholder">
                {metricLabel(selected)} is a headline figure for this preset but is not charted
                daily, so there is no series to draw.
              </p>
            </div>
          )}

          <Findings
            findings={data.findings}
            minimumSegmentUsers={data.profile.minimum_segment_users}
            minimumSegmentSharePercent={data.profile.minimum_segment_share_percent}
            thresholdPercent={data.profile.anomaly_threshold_percent}
            findingsMetric={data.profile.preset.findings_metric}
          />

          <p className="section__note" style={{ marginTop: 20 }}>
            {data.analyzed_rows.toLocaleString()} aggregate rows analysed locally · 0 raw rows
            processed · nothing sent outside this deployment.
          </p>
        </div>
      </div>
    </>
  )
}

function MetricRow({
  kpi,
  series,
  selected,
  onSelect,
}: {
  kpi: KPIResult
  series?: MetricSeries
  selected: boolean
  onSelect: () => void
}) {
  const tone = deltaTone(kpi.change_percent, kpi.name)

  return (
    <button
      type="button"
      className={`metrics__row${selected ? ' metrics__row--selected' : ''}`}
      aria-current={selected ? 'true' : undefined}
      onClick={onSelect}
    >
      <span className="metrics__row-label">
        <span className="metrics__row-name">{metricLabel(kpi.name)}</span>
        <span className={`metrics__row-delta tile__delta--${tone}`}>
          <span aria-hidden="true">{ARROW[tone]}</span> {formatChange(kpi.change_percent)}
        </span>
      </span>
      <span className="metrics__row-right">
        <span className="metrics__row-value">{formatMetric(kpi.name, kpi.current)}</span>
        <Sparkline series={series} muted={!selected} />
      </span>
    </button>
  )
}

/**
 * A sparkline for the list.
 *
 * Drawn by hand rather than with the chart library: this is a shape, not a chart —
 * no axes, no tooltip, no legend — and mounting a responsive container per row to
 * draw seven points would cost far more than the line is worth.
 */
function Sparkline({ series, muted }: { series?: MetricSeries; muted: boolean }) {
  if (!series || series.points.length < 2) return <span className="sparkline" />

  const values = series.points.map((point) => point.value)
  const low = Math.min(...values)
  const high = Math.max(...values)
  const span = high - low || 1
  const step = 100 / (values.length - 1)

  const points = values
    .map((value, index) => `${(index * step).toFixed(1)},${(20 - ((value - low) / span) * 18).toFixed(1)}`)
    .join(' ')

  return (
    <svg className="sparkline" viewBox="0 0 100 20" preserveAspectRatio="none" aria-hidden="true">
      <polyline
        points={points}
        fill="none"
        stroke={muted ? 'var(--series-comparison)' : 'var(--series-current)'}
        strokeWidth="1.5"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}
